import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function LocationConfig({ roles, onCreateTab, toastMessages, selectedLocation, setSelectedLocation, refresh, setRefresh }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Keep this list stable across renders to avoid unnecessary child rerenders
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Centralize CRUD route to avoid hard-coded duplicates
    const CRUD_ROUTE = '/home/locations/locationConfigCrud';
  
    //====================================================================================================
    // openLocationCrud:
    // - Stable (useCallback) handler to open/focus the Location CRUD tab.
    // - Persists 'operation' in global state; for View/Edit/Copy also persists the selected row.
    // - Validates presence of a row when required and shows a localized toast if missing.
    // - Builds the tab label using i18n and the location's Name.
    // - Using a stable callback avoids MTable refetches due to changing prop identities.
    //====================================================================================================
    const openLocationCrud = useCallback(
        (operation, row) => {
            // Persist operation for the CRUD screen
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // Ensure row/entity data is present for operations that need it
            if (['Edit', 'Copy', 'View'].includes(operation)) {
                if (!row) {
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'LocationLine', value: row }));
                // Optionally reflect selection locally if parent passes setters
                setSelectedLocation?.(row);
            }

            // Compute the tab label based on the chosen operation
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewLocation')
                    : operation === 'View'
                    ? `${t('LabelViewLocation')} "${row?.Name ?? ''}"`
                    : `${t('LabelEditLocation')} "${row?.Name ?? ''}"`;

                // Open/focus CRUD tab
            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages, setSelectedLocation]
    );

    //====================================================================================================
    // replaceFunctions:
    // - Memoized map of context actions consumed by MTable.
    // - useMemo keeps the Map identity stable across renders, preventing unnecessary rerenders
    //   and accidental duplicate fetches inside MTable.
    //====================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openLocationCrud('View', row)],
                ['Create', () => openLocationCrud('Create')],
                ['Edit', (row) => openLocationCrud('Edit', row)],
            ]),
        [openLocationCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div className="titleSettings" style={{ color: ctx.secondaryColor }}>{t('Locations')}</div>
                    <div className='labelSubtitleSettings'>{t("InfoLocations")}</div>
                </div>
            </div>
            <MTable // Main dynamic table component for displaying locations
                filtersOnTable
                entityName={'Locations'}
                roleName={'Locations'}
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management in the Locations Module
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // replaceFunctions for CRUD operations
            />
        </div>
    );
}

LocationConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    selectedLocation: PropTypes.any,
    setSelectedLocation: PropTypes.func,
    refresh: PropTypes.bool,
    setRefresh: PropTypes.func,
};
